/**
 * As regras puras das abas que vieram do dashboard do v1 (2026-10-25):
 * Visão Geral, Análise de Temas e Rankings. A tela só desenha; o que se
 * mostra, em que ordem e com que rótulo mora aqui, testado.
 */

import {
  DASHBOARD_NULL_KEY,
  type DashboardMetric,
  type SocialReportDashboard,
  type ThemeNoteRecorte
} from "@/shared/api/api-client";
import type { PolarityBarItem } from "@/shared/components/charts/PolarityBars";
import { humanizeLabel, metricLabel, type DashboardFacet, type DashboardFacets } from "@/shared/filters/snapshot-facets";
import { polarityOptions } from "@/shared/types/polarity";
import { formatCena, formatCompact, formatInteger } from "@/shared/utils/format";
import { formatCurrencyCompact } from "@/shared/utils/format-currency";

// ─── fonte ────────────────────────────────────────────────────────────

/** Qual fonte o dashboard mostra (2026-10-30): imprensa, redes ou as
 *  duas. Não é estado próprio — é a faceta `sources` lida de outro
 *  jeito, então o recorte vai pro servidor como qualquer filtro e a URL,
 *  a gaveta e a Análise de Temas continuam falando da mesma coisa. */
export type SourceView = "all" | "press" | "network";

export const SOURCE_VIEWS: Array<{ value: SourceView; label: string }> = [
  { value: "all", label: "Tudo" },
  { value: "press", label: "Imprensa" },
  { value: "network", label: "Redes" }
];

/** A faceta vista como fonte. Uma fonte só marcada é ela; marcada com
 *  "exceto" é a outra; nenhuma ou as duas é "Tudo". */
export function sourceViewOf(facet: DashboardFacet): SourceView {
  const values = [...facet.values];
  if (values.length !== 1) return "all";
  const only = values[0] === "press" ? "press" : "network";
  if (!facet.exclude) return only;
  return only === "press" ? "network" : "press";
}

export function sourceFacetFor(view: SourceView): DashboardFacet {
  return { values: new Set(view === "all" ? [] : [view]), exclude: false };
}

export type SourceVisibility = { press: boolean; network: boolean };

/** As fontes que o RELATÓRIO tem, pela resposta do servidor (não pelo
 *  recorte). Relatório vazio responde as duas: não há o que esconder. */
export function reportSources(data: SocialReportDashboard): SourceVisibility {
  const list = data.report_sources;
  if (!list.length) return { press: true, network: true };
  return { press: list.includes("press"), network: list.includes("network") };
}

/** Escolher fonte só faz sentido num relatório que tem as duas. */
export function offersSourceChoice(data: SocialReportDashboard): boolean {
  const has = reportSources(data);
  return has.press && has.network;
}

/** Que blocos de fonte aparecem. Com uma fonte escolhida, só ela; em
 *  "Tudo", só as que têm menção no recorte — um relatório só de redes
 *  não mostra gráfico de imprensa vazio. Recorte vazio mostra as duas
 *  (com o vazio de cada uma), pra tela não sumir inteira. */
export function visibleSources(data: SocialReportDashboard, view: SourceView): SourceVisibility {
  if (view !== "all") return { press: view === "press", network: view === "network" };
  const has = (key: string) => data.sources.some(item => item.key === key && item.count > 0);
  const press = has("press");
  const network = has("network");
  return press || network ? { press, network } : { press: true, network: true };
}

// ─── indicador ────────────────────────────────────────────────────────

/** O "indicador selecionado" do v1: o número que acompanha o volume. */
export type Indicator = "cena" | "interactions" | "reach" | "ad_value";

export const INDICATORS: Array<{ value: Indicator; label: string }> = [
  { value: "cena", label: "Score CENA" },
  { value: "interactions", label: "Engajamento" },
  { value: "reach", label: "Alcance potencial" },
  { value: "ad_value", label: "Valoração" }
];

export function indicatorLabel(indicator: Indicator): string {
  return INDICATORS.find(option => option.value === indicator)?.label ?? indicator;
}

export function indicatorValue(item: DashboardMetric, indicator: Indicator): number | null {
  if (indicator === "cena") return item.average_cena;
  if (indicator === "ad_value") return item.total_ad_value;
  return item[indicator];
}

/** O número escrito. Ausência é "—", nunca zero: sem nota ou sem matéria
 *  valorada não é "vale 0". */
export function indicatorText(item: DashboardMetric, indicator: Indicator): string {
  const value = indicatorValue(item, indicator);
  if (indicator === "cena") return formatCena(value);
  if (indicator === "ad_value") return formatCurrencyCompact(value);
  return value === null ? "—" : formatCompact(value);
}

// ─── quantidade ───────────────────────────────────────────────────────

export type Limit = "5" | "10" | "15" | "25" | "all";

export const LIMITS: Array<{ value: Limit; label: string }> = [
  { value: "5", label: "Top 5" },
  { value: "10", label: "Top 10" },
  { value: "15", label: "Top 15" },
  { value: "25", label: "Top 25" },
  { value: "all", label: "Todos" }
];

export function limited<T>(items: T[], limit: Limit): T[] {
  return limit === "all" ? items : items.slice(0, Number(limit));
}

// ─── rótulos ──────────────────────────────────────────────────────────

/** `MEDIA_TYPES` do `core/vocabulary.py`. */
export const MEDIA_TYPE_LABELS: Record<string, string> = {
  print: "Impresso",
  online: "Online",
  tv: "TV",
  radio: "Rádio",
  [DASHBOARD_NULL_KEY]: "Não informado"
};

const FORMAT_LABELS: Record<string, string> = { P: "Pequeno", M: "Médio", G: "Grande" };

/** `raw` é nome próprio (veículo, rede, tema, porta-voz): vai como veio,
 *  sem o "title case" que serve ao vocabulário interno. */
export type LabelKind = "media" | "format" | "author" | "raw" | "default";

export function sliceLabel(item: DashboardMetric, kind: LabelKind = "default"): string {
  if (kind === "media") return MEDIA_TYPE_LABELS[item.key] ?? humanizeLabel(item.key);
  if (kind === "format") return FORMAT_LABELS[item.key] ?? metricLabel(item);
  if (kind === "author") return item.parent ? `${item.key} · ${item.parent}` : item.key;
  if (kind === "raw") return item.key === DASHBOARD_NULL_KEY ? "Não informado" : item.label || item.key;
  return metricLabel(item);
}

/** As fatias no formato das barras empilhadas. Sem `indicator`, sem
 *  coluna de valor. */
export function toBarItems(
  items: DashboardMetric[],
  { indicator, kind = "default" }: { indicator?: Indicator; kind?: LabelKind } = {}
): PolarityBarItem[] {
  return items.map(item => ({
    key: item.parent ? `${item.parent}|${item.key}` : item.key,
    label: sliceLabel(item, kind),
    count: item.count,
    byPolarity: item.by_polarity,
    ...(indicator ? { metric: indicatorText(item, indicator) } : {})
  }));
}

/** Tira o balde "sem a dimensão" — formato em rede, por exemplo, não é
 *  uma categoria, é "não se aplica". */
export function withoutNull(items: DashboardMetric[]): DashboardMetric[] {
  return items.filter(item => item.key !== DASHBOARD_NULL_KEY);
}

// ─── destaques ────────────────────────────────────────────────────────

/** Os cinco cartões abaixo do gráfico no v1: os maiores pelo indicador
 *  escolhido (volume como desempate), sem quem não tem o número. */
export function topHighlights(items: DashboardMetric[], indicator: Indicator | "count", size = 5): DashboardMetric[] {
  const value = (item: DashboardMetric) => (indicator === "count" ? item.count : indicatorValue(item, indicator));
  return [...items]
    .filter(item => item.key !== DASHBOARD_NULL_KEY && value(item) !== null)
    .sort((a, b) => (value(b) ?? 0) - (value(a) ?? 0) || b.count - a.count)
    .slice(0, size);
}

// ─── ranking ──────────────────────────────────────────────────────────

export type RankingMetric = "count" | "cena" | "interactions" | "reach";

export const RANKING_METRICS: Array<{ value: RankingMetric; label: string }> = [
  { value: "count", label: "Volume" },
  { value: "cena", label: "CENA" },
  { value: "interactions", label: "Engajamento" },
  { value: "reach", label: "Alcance" }
];

/** O ranking de uma dimensão pela métrica escolhida. CENA sem nota vai
 *  pro fim (não é zero). */
export function ranked(items: DashboardMetric[], metric: RankingMetric): Array<{ item: DashboardMetric; value: number | null }> {
  const value = (item: DashboardMetric) => (metric === "count" ? item.count : metric === "cena" ? item.average_cena : item[metric]);
  return items
    .filter(item => item.key !== DASHBOARD_NULL_KEY)
    .map(item => ({ item, value: value(item) }))
    .sort((a, b) => {
      if (a.value === null && b.value === null) return b.item.count - a.item.count;
      if (a.value === null) return 1;
      if (b.value === null) return -1;
      return b.value - a.value || b.item.count - a.item.count;
    });
}

export function rankingText(value: number | null, metric: RankingMetric): string {
  if (metric === "cena") return formatCena(value);
  if (value === null) return "—";
  return metric === "count" ? formatInteger(value) : formatCompact(value);
}

// ─── Análise de Temas ─────────────────────────────────────────────────

/** O recorte de temas da aba, na forma CANÔNICA do servidor
 *  (`web_api/application/theme_notes.py::canonical_recorte`): valores
 *  aparados, sem repetição e em ordem; dimensão vazia fora. É por ela
 *  que a tela acha o texto gravado do recorte aberto. */
export function themeRecorte(facets: DashboardFacets): ThemeNoteRecorte {
  const result: ThemeNoteRecorte = {};
  for (const dimension of ["themeGroups", "themes", "sources"] as const) {
    const values = [...new Set([...facets[dimension].values].map(value => value.trim()).filter(Boolean))].sort();
    if (values.length) result[dimension] = { values, exclude: facets[dimension].exclude };
  }
  return result;
}

/** Uma chave de comparação, montada em ordem fixa: o recorte que volta do
 *  servidor passou pelo `jsonb`, que reordena as chaves. */
export function recorteKey(recorte: ThemeNoteRecorte): string {
  return JSON.stringify(
    (["sources", "themeGroups", "themes"] as const)
      .filter(dimension => recorte[dimension]?.values.length)
      .map(dimension => [dimension, [...(recorte[dimension]?.values ?? [])].sort(), Boolean(recorte[dimension]?.exclude)])
  );
}

/** Os números da Análise de Temas. Percentuais de sentimento sobre as
 *  menções CLASSIFICADAS (a regra da Visão Geral): menção sem polaridade
 *  não é neutra. */
export function themeKpis(data: SocialReportDashboard) {
  const sentiments = data.sentiments;
  const toneCount = (tone: "positive" | "negative") => {
    const names = new Set(polarityOptions().filter(option => option.tone === tone).map(option => option.name));
    return sentiments.filter(item => names.has(item.key)).reduce((sum, item) => sum + item.count, 0);
  };
  const classified = sentiments.filter(item => item.key !== DASHBOARD_NULL_KEY).reduce((sum, item) => sum + item.count, 0);
  const protagonist = data.approaches.find(item => item.key === "protagonist")?.count ?? 0;
  const share = (part: number, whole: number) => (whole ? Math.round((part / whole) * 100) : null);
  return {
    mentions: data.total_mentions,
    interactions: data.total_interactions,
    reach: data.total_reach,
    protagonistShare: share(protagonist, data.total_mentions),
    positiveShare: share(toneCount("positive"), classified),
    negativeShare: share(toneCount("negative"), classified),
    positive: toneCount("positive"),
    negative: toneCount("negative")
  };
}

/** O rascunho dos textos, montado com os números reais — no v1 a frase
 *  era fixa ("resultados estratégicos e impacto social") e igual pra todo
 *  cliente. Aqui é ponto de partida: quem escreve a análise é a pessoa. */
export function draftThemeText(data: SocialReportDashboard, tone: "positive" | "negative"): string {
  const kpis = themeKpis(data);
  const names = new Set(polarityOptions().filter(option => option.tone === tone).map(option => option.name));
  const count = tone === "positive" ? kpis.positive : kpis.negative;
  const share = tone === "positive" ? kpis.positiveShare : kpis.negativeShare;
  const channels = [...data.press_channels, ...data.network_channels]
    .map(item => ({ label: item.key, value: Object.entries(item.by_polarity ?? {}).filter(([key]) => names.has(key)).reduce((sum, [, value]) => sum + value, 0) }))
    .filter(item => item.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, 3)
    .map(item => item.label);
  const themes = data.themes
    .map(item => ({ label: item.key, value: Object.entries(item.by_polarity ?? {}).filter(([key]) => names.has(key)).reduce((sum, [, value]) => sum + value, 0) }))
    .filter(item => item.value > 0 && item.label !== DASHBOARD_NULL_KEY)
    .sort((a, b) => b.value - a.value)
    .slice(0, 3)
    .map(item => item.label);
  if (!count) {
    return tone === "positive"
      ? "Nenhuma menção positiva ou favorável neste recorte."
      : "Nenhuma menção negativa ou desfavorável neste recorte.";
  }
  const word = tone === "positive" ? "positivas ou favoráveis" : "negativas ou desfavoráveis";
  const parts = [`${formatInteger(count)} menções ${word}${share !== null ? ` (${share}% das classificadas)` : ""}.`];
  if (themes.length) parts.push(`Subtemas que mais concentraram: ${themes.join(", ")}.`);
  if (channels.length) parts.push(`Veículos e canais com mais menções: ${channels.join(", ")}.`);
  return parts.join(" ");
}
